import { useState, FormEvent, CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { getDeviceId } from '../device';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const [form, setForm] = useState({
    fullName: '', username: '', password: '', confirmPassword: '',
    email: '', phone: '', referralCode: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [policyContent, setPolicyContent] = useState('');
  const [policyLoading, setPolicyLoading] = useState(false);

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }));

  const openPolicy = async () => {
    setShowPolicy(true);
    if (policyContent) return;
    setPolicyLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/privacy-policy`);
      setPolicyContent(res.data.content || 'No policy content available.');
    } catch {
      setPolicyContent('Failed to load privacy policy. Please try again later.');
    } finally {
      setPolicyLoading(false);
    }
  };

  const handleRegister = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.fullName.trim()) return toast.error('Missing Field', 'Please enter your full name.');
    if (!form.username.trim()) return toast.error('Missing Field', 'Please choose a username.');
    if (form.password.length < 6) return toast.error('Weak Password', 'Password must be at least 6 characters.');
    if (form.password !== form.confirmPassword) return toast.error('Password Mismatch', 'Passwords do not match.');
    if (!form.email.trim()) return toast.error('Missing Field', 'Please enter your email address.');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) return toast.error('Invalid Email', 'Please enter a valid email address.');
    if (!form.phone.trim()) return toast.error('Missing Field', 'Please enter your phone number.');
    if (!agreedToTerms) return toast.error('Terms Required', 'Please accept the terms and privacy policy.');

    setLoading(true);
    try {
      const deviceId = await getDeviceId().catch(() => null);
      const res = await axios.post(
        `${API_BASE}/auth/register`,
        {
          fullName: form.fullName.trim(),
          username: form.username.trim().toLowerCase(),
          password: form.password,
          email: form.email.trim(),
          phone: form.phone.trim(),
          referralCode: form.referralCode.trim().toUpperCase() || undefined,
        },
        deviceId ? { headers: { 'X-Device-Id': deviceId } } : undefined,
      );
      const { access_token, refresh_token, user } = res.data;
      await login(access_token, user.id, refresh_token);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      const status = e.response?.status;
      if (status === 409) toast.error('Username Taken', 'That username is already in use.');
      else if (status === 403) toast.error('Device Limit Reached', e.response?.data?.message || 'This device has already registered the maximum number of accounts.');
      else toast.error('Registration Failed', e.response?.data?.message || 'Could not create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, padding: '24px max(24px, env(safe-area-inset-left))' }}>
      <div style={{ width: '100%', maxWidth: 460, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/login')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: colors.textMuted, fontSize: 14, marginBottom: 20, minHeight: 44,
          }}
        >
          <Icon name="arrow-left" size={16} color={colors.textMuted} />
          Back to Sign In
        </button>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/icons/icon-512.png" alt="" style={{ width: 72, height: 72, marginBottom: 10, objectFit: 'contain' }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: colors.text }}>Create Account</div>
          <div style={{ fontSize: 13, color: colors.textSub, marginTop: 3 }}>Join AGA — All Game Arena for free</div>
        </div>

        <form
          onSubmit={handleRegister}
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: 16,
            padding: 22,
          }}
        >
          <Field label="Full Name">
            <Icon name="user" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="Your full name"
              value={form.fullName} onChange={e => set('fullName', e.target.value)} autoCapitalize="words" />
          </Field>
          <Field label="Username">
            <Icon name="at" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="Choose a username"
              value={form.username} onChange={e => set('username', e.target.value.toLowerCase())} autoCapitalize="none" autoCorrect="off" />
          </Field>
          <Field label="Password">
            <Icon name="lock" size={15} color={colors.textSub} style={iconPad} />
            <input style={{ ...inputStyle(colors.inputText), flex: 1 }} placeholder="At least 6 characters"
              type={showPass ? 'text' : 'password'} value={form.password}
              onChange={e => set('password', e.target.value)} autoCapitalize="none" />
            <button type="button" aria-label={showPass ? 'Hide password' : 'Show password'} onClick={() => setShowPass(v => !v)}
              style={eyeBtnStyle}>
              <Icon name={showPass ? 'eye-slash' : 'eye'} size={15} color={colors.textSub} />
            </button>
          </Field>
          <Field label="Confirm Password">
            <Icon name="lock" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="Re-enter password"
              type={showPass ? 'text' : 'password'} value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)} autoCapitalize="none" />
          </Field>
          <Field label="Email Address *">
            <Icon name="envelope" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="your@email.com"
              type="email" value={form.email} onChange={e => set('email', e.target.value)} autoCapitalize="none" autoComplete="email" />
          </Field>
          <Field label="Mobile Phone *">
            <Icon name="phone" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="+1 000 000 0000"
              type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" />
          </Field>
          <Field label="Referral Code (optional)">
            <Icon name="gift" size={15} color={colors.textSub} style={iconPad} />
            <input style={inputStyle(colors.inputText)} placeholder="Enter friend's code"
              maxLength={16} value={form.referralCode}
              onChange={e => set('referralCode', e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" />
          </Field>

          <Button label="Create Account" icon="user-plus" loading={loading} type="submit" style={{ marginTop: 24 }} />

          <div
            onClick={() => setAgreedToTerms(v => !v)}
            role="checkbox"
            aria-checked={agreedToTerms}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, marginTop: 16,
              minHeight: 44, cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: 20, height: 20, borderRadius: 5,
                border: `2px solid ${agreedToTerms ? '#14b8a6' : '#475569'}`,
                backgroundColor: agreedToTerms ? '#14b8a6' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {agreedToTerms && <Icon name="check" size={11} color="#fff" />}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.5, flex: 1 }}>
              I accept the{' '}
              <span
                onClick={e => { e.stopPropagation(); openPolicy(); }}
                style={{ color: '#60a5fa', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Terms &amp; Privacy Policy
              </span>
            </div>
          </div>
        </form>
      </div>

      <Modal open={showPolicy} onClose={() => setShowPolicy(false)} contentStyle={{ maxHeight: '80%' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Privacy Policy</div>
        <div style={{ maxHeight: 400, overflowY: 'auto', fontSize: 14, lineHeight: 1.55, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
          {policyLoading ? <div style={{ textAlign: 'center', padding: 20 }}>Loading…</div> : policyContent}
        </div>
        <Button label="Close" onClick={() => setShowPolicy(false)} style={{ marginTop: 16 }} />
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  const { colors } = useTheme();
  return (
    <div>
      <div
        style={{
          fontSize: 12, fontWeight: 700, color: colors.textMuted,
          marginBottom: 6, marginTop: 14, letterSpacing: 0.3, textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'center',
          backgroundColor: colors.input,
          border: `1px solid ${colors.inputBorder}`,
          borderRadius: 8, minHeight: 50,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const iconPad: CSSProperties = { paddingLeft: 14, paddingRight: 10 };
const eyeBtnStyle: CSSProperties = {
  width: 50, minHeight: 50, background: 'transparent', border: 'none',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const inputStyle = (color: string): CSSProperties => ({
  flex: 1, padding: '13px 14px 13px 0',
  fontSize: 15, color, background: 'transparent', border: 'none', outline: 'none', width: '100%',
});
